/**
 * App shell — three-zone diagnostic-viewer chrome with role-gated nav.
 */
import type { JSX, ParentProps } from "solid-js";
import { A, useLocation, useNavigate } from "@solidjs/router";
import { Show, For, createMemo, createEffect, createSignal, onCleanup } from "solid-js";
import { locale, setLocale, t, type Locale } from "./i18n";
import { auth, displayName, isLoggedIn, canAdmin, canAudit, setAuthState } from "./auth/store";
import { logout } from "./auth/oidc";
import type { PacsAction } from "./auth/types";
import { can as canFn } from "./auth/types";
import { ShortcutHelp, toggleShortcuts } from "./app/ShortcutHelp";
import { BrandedDialogHost } from "./app/branded-dialog";
import { activeStudy } from "./app/studyContext";
import { modalityTitle } from "./core/modality";
import { singleSpecialty } from "./auth/scope";
import type { Specialty } from "./specialty/config";
import { IconScope } from "./ui/Icons";
import { SettingsPanel } from "./settings/SettingsPanel";

/** Icon can be either a short emoji/text glyph or a JSX node (for
 *  custom SVG icons). The renderer detects type at runtime. */
type NavIcon = string | (() => JSX.Element);

type NavItem = {
  path: string;
  label: string;
  icon: NavIcon;
  gate?: PacsAction;
};

// The Reading Room + Bench nav entries are shared across specialties
// but the icon + label should read right for whichever specialty the
// user actually works in. A pathologist shouldn't see an X-ray emoji
// next to their microscopy worklist. Radiotherapist should see a
// radiation icon, endoscopist a scope, etc.
const READING_BY_SPECIALTY: Record<Specialty, { label: string; icon: NavIcon }> = {
  radiology:    { label: "Reading room",  icon: "🩻" },
  pathology:    { label: "Microscopy",    icon: "🔬" },
  // Custom SVG — a generic scope (handpiece + flexible insertion tube
  // + distal lens) rather than the 🫁 lung emoji, which incorrectly
  // implied "bronchoscopy only" for what is an umbrella specialty
  // covering GI, airway, urologic, gynaecological, and orthopaedic
  // scopic procedures.
  endoscopy:    { label: "Endoscopy",     icon: () => <IconScope /> },
  radiotherapy: { label: "Plan review",   icon: "☢"  },
};
const BENCH_BY_SPECIALTY: Record<Specialty, { label: string; icon: NavIcon }> = {
  radiology:    { label: "Bench",            icon: "🛠" },
  pathology:    { label: "Slide lab",        icon: "🧪" },
  endoscopy:    { label: "Endoscopy suite",  icon: () => <IconScope /> },
  radiotherapy: { label: "Linac bench",      icon: "🛰" },
};

// Role-aware navigation under the 4-role matrix. `gate` is the
// minimum matrix action the user must hold for the item to appear.
// /techbench, /referrals, /portal are tied to roles that no longer
// exist (technologist, referring_physician, patient) and are hidden
// in the visibleNav() filter below until the next-gen UI lands.
const BASE_NAV: NavItem[] = [
  { path: "/worklist",  label: "Worklist",     icon: "📋", gate: "studies.read" },
  { path: "/reading",   label: "Reading room", icon: "🩻", gate: "reports.draft" },
  { path: "/techbench", label: "Bench",        icon: "🛠", gate: "studies.read" },
  { path: "/referrals", label: "My patients",  icon: "👤", gate: "studies.read" },
  { path: "/portal",    label: "My studies",   icon: "📂", gate: "studies.read" },
  { path: "/studies",   label: "Studies",      icon: "📁", gate: "studies.read" },
  { path: "/reports",   label: "Reports",      icon: "🗂", gate: "studies.read" },
  { path: "/audit",     label: "Audit",        icon: "🔎", gate: "audit.read"   },
  { path: "/admin",     label: "Admin",        icon: "⚙",  gate: "users.read"   },
];


export function App(props: ParentProps) {
  const loc = useLocation();
  const nav = useNavigate();
  // The nav is specialty-skinned: the Reading Room + Bench entries
  // swap their icon + label to match the user's single specialty
  // (pathology → microscope, endoscopy → scope, RT → radiation).
  // Users with multi-specialty grants fall back to the base labels.
  const skinnedNav = createMemo<NavItem[]>(() => {
    const s = singleSpecialty(auth());
    if (!s) return BASE_NAV;
    return BASE_NAV.map((n) => {
      if (n.path === "/reading")   return { ...n, ...READING_BY_SPECIALTY[s] };
      if (n.path === "/techbench") return { ...n, ...BENCH_BY_SPECIALTY[s] };
      return n;
    });
  });
  const current = createMemo(() => skinnedNav().find((n) => n.path === loc.pathname) ?? skinnedNav()[0]);
  const onLoginRoute = () => loc.pathname === "/login" || loc.pathname === "/callback";

  // Collapsible left rail (MedDream-style). Icon-only when collapsed.
  // Persist the choice so a radiologist's preference sticks across
  // reloads without needing a server round-trip.
  const initial = (() => {
    try { return localStorage.getItem("vpd.navCollapsed") === "1"; } catch { return false; }
  })();
  const [navCollapsed, setNavCollapsed] = createSignal(initial);
  const [settingsOpen, setSettingsOpen] = createSignal(false);
  createEffect(() => {
    try { localStorage.setItem("vpd.navCollapsed", navCollapsed() ? "1" : "0"); } catch { /* fine */ }
  });

  createEffect(() => {
    if (!isLoggedIn() && !onLoginRoute()) {
      nav("/login", { replace: true });
    }
  });

  // --- Idle-timeout watchdog (Phase D.2) ---
  // Reads the admin-configured `idle_timeout_minutes` from
  // /viewer-api/policy/client, then signs the reader out after
  // that many minutes of no pointer/key activity. Every timeout
  // fires an `auth.session_timeout` audit row before the redirect
  // so the reviewer can distinguish idle-out from user-clicked
  // sign-out.
  {
    let idleMs = 15 * 60 * 1000; // default 15 min, overwritten on fetch
    let timer: number | undefined;
    const reset = () => {
      if (timer) window.clearTimeout(timer);
      if (!isLoggedIn()) return;
      timer = window.setTimeout(async () => {
        if (!isLoggedIn()) return;
        // Fire-and-forget audit + end_session. The server side logs
        // auth.logout; we tag it with `reason: idle_timeout` via the
        // dedicated /audit/emit hook so review can filter for it.
        try {
          const raw = sessionStorage.getItem("vpd.auth.v1");
          const tok = raw ? JSON.parse(raw).accessToken : null;
          if (tok) {
            await fetch("/viewer-api/audit/emit", {
              method: "POST",
              headers: { "content-type": "application/json", authorization: `Bearer ${tok}` },
              body: JSON.stringify({ kind: "auth.session_timeout",
                                     object_ref: "(idle)",
                                     metadata: { idle_ms: idleMs } }),
            }).catch(() => {});
          }
        } catch {}
        setAuthState(null);
        logout();
      }, idleMs);
    };
    const events = ["pointerdown", "keydown", "wheel", "touchstart", "visibilitychange"] as const;
    for (const ev of events) window.addEventListener(ev, reset, { passive: true });
    fetch("/viewer-api/policy/client")
      .then((r) => r.ok ? r.json() : null)
      .then((cfg) => {
        if (cfg && Number.isFinite(cfg.idle_timeout_minutes)) {
          idleMs = Math.max(60_000, cfg.idle_timeout_minutes * 60_000);
          reset();
        }
      })
      .catch(() => { /* stay on default */ });
    createEffect(() => {
      // Re-arm on login state change so signing-in immediately
      // starts the idle clock.
      isLoggedIn();
      reset();
    });
    onCleanup(() => {
      if (timer) window.clearTimeout(timer);
      for (const ev of events) window.removeEventListener(ev, reset);
    });
  }

  // Filter in two passes: (a) drop items whose `gate` action the
  // current user can't perform (e.g. audit.read, admin.users), and
  // (b) drop items that are redundant for this role set (e.g. /studies
  // for a pure patient who already has /portal).
  const visibleNav = createMemo(() => {
    const a = auth();
    if (!a) return [];
    const gated = skinnedNav().filter((n) => !n.gate || canFn(a, n.gate));
    const roleSet = new Set<string>(a.roles);
    const is = (x: string) => roleSet.has(x);
    // 4-role matrix: User-UI roles (owner/senior/junior) get the
    // Reading room; admin-only users do not. The legacy /referrals,
    // /portal, /techbench surfaces are tied to roles that are gone
    // (referring_physician, patient, technologist) — hide them in
    // every case until the next-gen UI lands.
    const isReader = () => is("owner") || is("senior") || is("junior");
    const isAdminOnly = () => is("admin") && !is("owner") && !is("senior") && !is("junior");
    const isClinicalOnly = () => (is("senior") || is("junior")) && !is("owner") && !is("admin");
    return gated.filter((n) => {
      if (n.path === "/referrals" || n.path === "/portal" || n.path === "/techbench") return false;
      if (n.path === "/studies"   && !isReader()) return false;
      if (n.path === "/reading"   && !isReader()) return false;
      // Admin-only sees Worklist (a triage / scheduler surface — they
      // route incoming studies before a radiologist picks them up);
      // /reports stays clinical-only.
      if (isAdminOnly() && n.path === "/reports") return false;
      if (isClinicalOnly() && n.path === "/studies") return false;
      return true;
    });
  });

  return (
    <div class="flex h-full flex-col bg-ink-50 text-ink-1000">
      <BrandedDialogHost />
      <Header />
      <Show when={!onLoginRoute()} fallback={<main class="flex-1 overflow-hidden">{props.children}</main>}>
        <div class="flex min-h-0 flex-1">
          <aside class="relative flex shrink-0 flex-col border-r border-ink-300 bg-ink-100 transition-[width] duration-150"
                 classList={{ "w-56": !navCollapsed(), "w-12": navCollapsed() }}
                 aria-label="Primary navigation">
            <button type="button"
                    onClick={() => setNavCollapsed((v) => !v)}
                    aria-expanded={!navCollapsed()}
                    aria-label={navCollapsed() ? "Expand navigation" : "Collapse navigation"}
                    title={navCollapsed() ? "Expand" : "Collapse"}
                    class="absolute -right-3 top-3 z-10 grid h-6 w-6 place-items-center rounded-full border border-ink-300 bg-ink-100 text-ink-700 shadow hover:text-white">
              <span class="text-xs">{navCollapsed() ? "›" : "‹"}</span>
            </button>
            <nav class="flex flex-col gap-0.5 p-2" role="navigation">
              <For each={visibleNav()}>
                {(item) => (
                  <A
                    href={item.path}
                    class="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-ink-800 transition hover:bg-ink-200 hover:text-white"
                    activeClass="bg-ink-200 text-white ring-1 ring-brand/40"
                    end={item.path === "/"}
                    aria-current={item.path === loc.pathname ? "page" : undefined}
                    title={navCollapsed() ? item.label : undefined}
                    classList={{ "justify-center !px-0": navCollapsed() }}
                  >
                    {/* Icon can be a glyph string (emoji) OR a JSX
                        component (custom SVG). Custom SVGs render at
                        h-5 w-5 currentColor; emoji strings render at
                        text-base so they size consistently. */}
                    <Show when={typeof item.icon === "function"} fallback={
                      <span class="text-base leading-none" aria-hidden="true">{item.icon as string}</span>
                    }>
                      <span class="inline-flex h-5 w-5 items-center justify-center" aria-hidden="true">
                        {(item.icon as () => JSX.Element)()}
                      </span>
                    </Show>
                    <Show when={!navCollapsed()}>
                      <span>{item.label}</span>
                    </Show>
                  </A>
                )}
              </For>
            </nav>
            <Show when={!navCollapsed()}>
              <div class="mt-auto p-2 text-[11px] leading-4 text-ink-700">
                <div class="border-t border-ink-300 pt-2">
                  <span class="chip-brand">live</span>
                  <div class="mt-2">
                    host <span class="font-mono text-ink-900">{typeof window !== "undefined" ? window.location.hostname : ""}</span>
                  </div>
                  <Show when={auth()}>
                    <div class="mt-3 border-t border-ink-300 pt-2">
                      <div class="text-ink-900">{displayName()}</div>
                      <div class="flex flex-wrap gap-1 pt-1">
                        <For each={auth()!.roles}>{(r) => (
                          <span class="chip-brand !text-[9px]">{r}</span>
                        )}</For>
                      </div>
                      <Show when={canAudit()}>
                        <div class="mt-1 text-[10px] text-ink-700">audit: ON</div>
                      </Show>
                      <Show when={canAdmin()}>
                        <div class="mt-1 text-[10px] text-ink-700">admin: ON</div>
                      </Show>
                    </div>
                  </Show>
                </div>
              </div>
            </Show>
          </aside>
          <main class="min-w-0 flex-1 overflow-hidden">
            <div class="flex h-full flex-col">
              <BreadCrumbs />
              <section class="flex-1 overflow-auto">{props.children}</section>
            </div>
          </main>
        </div>
      </Show>
      <StatusBar />
      <ShortcutHelp />
      <SettingsPanel open={settingsOpen()} onClose={() => setSettingsOpen(false)} />
    </div>
  );

  function Header() {
    return (
      <header class="flex items-center gap-3 border-b border-ink-300 bg-ink-100 px-4 py-2 shadow-panel">
        <a href="/" class="flex items-center gap-2">
          <img src="/logo.png?v=2" width="22" height="22" alt="medpod" class="shrink-0" />
          <div class="leading-tight">
            <div class="text-sm font-semibold tracking-wide">
              <span class="text-brand">med</span>
              <span class="text-white">pod</span>
              <span class="ml-2 text-[11px] uppercase tracking-wider text-ink-700">radiology</span>
            </div>
          </div>
        </a>
        <span class="ml-4 hidden h-6 w-px bg-ink-300 md:block" />
        <div class="hidden min-w-0 flex-1 items-center gap-4 md:flex">
          <PatientBanner />
        </div>
        <div class="flex items-center gap-2">
          <Show
            when={auth()}
            fallback={
              <span class="pill" aria-label="not signed in">not signed in</span>
            }
          >
            <span class="chip-brand" title={auth()!.claims.sub}>{displayName()}</span>
            {/* Cog-wheel — opens the Settings panel. Sits at the
                same level as Sign out + user chip. Language toggle
                lives inside this panel now; future profile +
                organisation fields, printer defaults, etc. will
                land there too. */}
            <button type="button"
                    class="btn-ghost !p-1.5"
                    title="Settings"
                    aria-label="Settings"
                    onClick={() => setSettingsOpen(true)}>
              <span class="inline-block h-4 w-4">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"
                     stroke-linecap="round" stroke-linejoin="round"
                     class="h-full w-full" aria-hidden="true">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
              </span>
            </button>
            <button type="button" class="btn-ghost" onClick={() => { setAuthState(null); logout(); }} aria-label="Sign out">
              Sign out
            </button>
          </Show>
        </div>
      </header>
    );
  }

  function PatientBanner() {
    const study = activeStudy;
    return (
      <Show when={study()} fallback={<div class="text-xs text-ink-700">{auth() ? "No active study" : ""}</div>}>
        {(s) => (
          <div class="flex min-w-0 items-center gap-3 text-xs">
            <span class="chip-brand !text-[9px]"
                  title={modalityTitle(s().modality)}>
              {s().modality ?? "—"}
            </span>
            <div class="truncate">
              <span class="font-medium text-ink-900">{s().patientName ?? "Unknown"}</span>
              <span class="mx-2 text-ink-700">·</span>
              <span class="text-ink-700">MRN</span>{" "}
              <span class="font-mono">{s().patientId ?? "—"}</span>
              <Show when={s().patientDob}>
                <span class="mx-2 text-ink-700">·</span>
                <span class="text-ink-700">DOB</span>{" "}
                <span class="font-mono">{s().patientDob}</span>
              </Show>
              <Show when={s().patientSex}>
                <span class="mx-2 text-ink-700">·</span>
                <span class="font-mono">{s().patientSex}</span>
              </Show>
            </div>
            <Show when={s().instanceCount}>
              <span class="ml-auto whitespace-nowrap text-ink-700">
                slice <span class="font-mono text-ink-900">{s().activeInstance ?? 1}</span>
                {" / "}
                <span class="font-mono">{s().instanceCount}</span>
              </span>
            </Show>
          </div>
        )}
      </Show>
    );
  }

  function LocaleSwitcher() {
    const opts: Array<{ v: Locale; label: string }> = [
      { v: "en", label: "EN" },
      { v: "vi", label: "VI" },
    ];
    return (
      <div class="inline-flex rounded-md border border-ink-300" role="group" aria-label="Language">
        <For each={opts}>{(o) => (
          <button
            type="button"
            class="px-2 py-1 text-[11px] uppercase tracking-wider"
            classList={{
              "bg-brand text-white": locale() === o.v,
              "text-ink-700 hover:text-white": locale() !== o.v,
            }}
            onClick={() => setLocale(o.v)}
            aria-pressed={locale() === o.v}
          >
            {o.label}
          </button>
        )}</For>
      </div>
    );
  }

  function BreadCrumbs() {
    return (
      <nav class="flex items-center gap-2 border-b border-ink-300 bg-ink-100 px-4 py-1.5 text-xs text-ink-700"
           aria-label="Breadcrumb">
        <span>medpod</span>
        <span aria-hidden="true">›</span>
        <span class="text-ink-900" aria-current="page">{current().label}</span>
        <span class="ml-auto">
          <button class="btn-ghost !py-0 !px-2 text-[10px]"
                  onClick={toggleShortcuts}
                  title="Keyboard shortcuts (?)">
            shortcuts <kbd>?</kbd>
          </button>
        </span>
      </nav>
    );
  }

  function StatusBar() {
    return (
      <footer class="flex items-center justify-between border-t border-ink-300 bg-ink-100 px-4 py-1 text-[11px] text-ink-700">
        <div class="flex min-w-0 items-center gap-2 truncate">
          <a href="mailto:contact@cloudkites.com" class="truncate hover:text-brand">
            contact@cloudkites.com
          </a>
          <span class="hidden md:inline" aria-hidden="true">·</span>
          <a href="/sbom.json"
             class="hidden hover:text-brand md:inline"
             target="_blank" rel="noreferrer"
             title="CycloneDX SBOM">
            SBOM
          </a>
          <span class="hidden md:inline" aria-hidden="true">·</span>
          <span class="hidden md:inline" title="Container image signed with cosign">
            <span class="chip-ok !text-[9px]">cosign</span>
          </span>
        </div>
        <div class="flex items-center gap-3 font-mono">
          <span>Δ connect 0 ms</span>
          <span>·</span>
          <span>TLS 1.3</span>
        </div>
      </footer>
    );
  }
}
